/**
 * ч.1425: оферта і політика конфіденційності у вікні поверх сторінки.
 *
 * Тексти — статичні `public/legal/*.html`: той самий файл відкривається з головної сторінки, з
 * реєстрації і з підтвердження організації, тож редакція одна. Посилання нікуди не веде зі сторінки:
 * на реєстрації людина не втрачає вже введене.
 */
import { useEffect, useState, type ReactNode } from 'react';

import BodyPortal from '@/shared/components/BodyPortal';

const DOCS = {
  terms: { title: 'Умови використання (публічна оферта)', src: '/legal/terms.html' },
  privacy: { title: 'Політика конфіденційності', src: '/legal/privacy.html' },
} as const;

export function LegalDocLink({ doc, children }: { doc: keyof typeof DOCS; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <>
      <button type="button" className="btn-link" onClick={() => setOpen(true)}>{children}</button>
      {open && (
        <BodyPortal>
          <div className="legal-modal" role="dialog" aria-modal="true" aria-label={DOCS[doc].title}
            onClick={() => setOpen(false)}>
            <div className="legal-modal__panel" onClick={(e) => e.stopPropagation()}>
              <div className="legal-modal__head">
                <strong>{DOCS[doc].title}</strong>
                <button type="button" onClick={() => setOpen(false)}>закрити</button>
              </div>
              <iframe className="legal-modal__text" title={DOCS[doc].title} src={DOCS[doc].src} />
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
}

/** Обовʼязкова позначка згоди: сервер без неї реєстрацію не приймає. */
export default function LegalConsent({ checked, onChange }: {
  checked: boolean; onChange: (value: boolean) => void;
}) {
  return (
    <label className="legal-consent">
      <input type="checkbox" required checked={checked} aria-label="Приймаю умови"
        onChange={(e) => onChange(e.target.checked)} />
      <span>
        Я приймаю <LegalDocLink doc="terms">Умови використання</LegalDocLink> і{' '}
        <LegalDocLink doc="privacy">Політику конфіденційності</LegalDocLink>
      </span>
    </label>
  );
}
